import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useOrg from '@/hooks/useOrg';
import LabelStyleEditor, { defaultEditableStyle } from '@/features/settings/LabelStyleEditor';
import { normalizeLabelStyle, resolveLabelStyle } from '@/features/map/labelStyle';
import api from '@/lib/api';
import { Edit3, X, Save, Loader2, Settings2 } from 'lucide-react';
import FuelCalibrationSection from '@/features/settings/FuelCalibrationSection';
import CustomFieldsSection from '@/components/shared/CustomFieldsSection';
import { resolveMarkerConfig, getIconDef } from '@/lib/markerIcons';
import MapMarkerPicker from '@/components/shared/MapMarkerPicker';
import DevicePhotos from '@/components/shared/DevicePhotos';
// isSupabaseConfigured removed — Trackizy Server is always available
import { showToast } from '@/components/shared/Toast';
import SensorConfigSection from '@/features/settings/SensorConfigSection';
import CanSignalsSection from '@/features/settings/CanSignalsSection';
import { editableAttributes, buildSavedAttributes } from '@/features/settings/deviceFormAttrs';
import { KNOWN_MODELS } from '@trackizy/formatters';

export default function DeviceForm({ device, onSave, onCancel, isAdmin, userId, compact }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const prefillImei = !device?.id ? (searchParams.get('newImei') ?? '') : '';
  const attemptId = searchParams.get('attemptId');
  const [name, setName] = useState(device?.name || '');
  const [uniqueId, setUniqueId] = useState(device?.uniqueId || prefillImei);
  const [phone, setPhone] = useState(device?.phone || '');
  const [model, setModel] = useState(device?.model || '');
  const [category, setCategory] = useState(device?.category || '');
  const [contact, setContact] = useState(device?.contact || '');
  const [disabled, setDisabled] = useState(device?.disabled || false);
  const [expirationTime, setExpirationTime] = useState(device?.expirationTime || '');
  const [groupId, setGroupId] = useState(device?.groupId || 0);
  const [calendarId, setCalendarId] = useState(device?.calendarId || 0);
  const markerConfig = resolveMarkerConfig(device?.attributes);
  const [markerStyle, setMarkerStyle] = useState(markerConfig.style);
  const [markerIcon, setMarkerIcon] = useState(markerConfig.icon);
  const [markerCustomUrl, setMarkerCustomUrl] = useState(markerConfig.customUrl);
  const [showMarkerPicker, setShowMarkerPicker] = useState(false);
  // Map label override (spec 2026-10-14-map-label-styles §6). null = inherit.
  const [labelStyle, setLabelStyle] = useState(() => normalizeLabelStyle(device?.attributes?.mapLabelStyle));
  const [editableAttrs, setEditableAttrs] = useState(() => editableAttributes(device?.attributes));
  const [saving, setSaving] = useState(false);

  const [groups, setGroups] = useState([]);
  const [calendars, setCalendars] = useState([]);

  const [showAccumulators, setShowAccumulators] = useState(false);
  const [accumDistance, setAccumDistance] = useState(0);
  const [accumHours, setAccumHours] = useState(0);
  const [accumSaving, setAccumSaving] = useState(false);

  const customFieldsRef = useRef(null);
  const groupsById = useSelector((state) => state.groups.items);
  const orgSettings = useOrg()?.settings;
  const inherited = resolveLabelStyle({ ...device, groupId, attributes: {} }, groupsById, orgSettings);
  const liveAttrs = useSelector((state) => state.session.positions?.[device?.id]?.attributes);
  const rawAttributeKeys = useMemo(() => Object.keys(liveAttrs || {}), [liveAttrs]);

  useEffect(() => {
    api.getGroups().then(setGroups).catch(() => {});
    api.getCalendars().then(setCalendars).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !uniqueId.trim()) return;
    const blocking = customFieldsRef.current?.validateBlockingForNewDevice();
    if (blocking) {
      showToast(blocking);
      return;
    }
    setSaving(true);
    try {
      const attrs = buildSavedAttributes(device?.attributes, editableAttrs);
      delete attrs.mapMarker;
      if (markerStyle === 'arrow' && !markerIcon) {
        delete attrs.mapMarkerStyle;
        delete attrs.mapMarkerIcon;
        delete attrs.mapIcon;
      } else {
        attrs.mapMarkerStyle = markerStyle;
        attrs.mapMarkerIcon = markerIcon;
        if (markerIcon === 'custom' && markerCustomUrl) {
          attrs.mapIcon = markerCustomUrl;
        } else {
          delete attrs.mapIcon;
        }
      }
      if (labelStyle) attrs.mapLabelStyle = labelStyle; else delete attrs.mapLabelStyle;
      const saved = await onSave({
        ...device,
        name: name.trim(),
        uniqueId: uniqueId.trim(),
        phone,
        model,
        category,
        contact: contact || undefined,
        disabled,
        expirationTime: expirationTime || null,
        groupId: groupId || 0,
        calendarId: calendarId || 0,
        attributes: attrs,
        ...(!device?.id && attemptId ? { attemptId } : {}),
      });
      const savedId = saved?.id ?? device?.id;
      if (savedId && customFieldsRef.current) {
        try {
          await customFieldsRef.current.save(savedId);
        } catch (e) {
          showToast(e.message);
        }
      } else if (!savedId) {
        showToast('Save the vehicle first, then reopen to edit custom fields.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAccumulators = () => {
    setAccumDistance(device?.totalDistance || 0);
    setAccumHours(device?.hours || 0);
    setShowAccumulators(true);
  };

  const handleSaveAccumulators = async () => {
    setAccumSaving(true);
    try {
      await api.updateDeviceAccumulators(device.id, {
        totalDistance: Number(accumDistance),
        hours: Number(accumHours),
      });
      setShowAccumulators(false);
    } catch (err) {
      showToast(t('common.failedSave') + ': ' + err.message);
    } finally {
      setAccumSaving(false);
    }
  };

  const categories = ['car', 'truck', 'bus', 'motorcycle', 'van', 'pickup', 'suv', 'boat', 'person', 'phone', 'tracker', 'asset', 'default'];

  const inputClass = 'w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10';
  const selectClass = 'w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10 bg-white';
  const labelClass = 'block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1';
  const sectionClass = 'text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-4';
  const gridCols = compact ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 lg:grid-cols-3';

  return (
    <>
      <form onSubmit={handleSubmit} className={compact ? 'space-y-5' : 'bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 p-5 space-y-5'}>
        <div>
          <h4 className={sectionClass}>{t('settings.basicInfo')}</h4>
          <div className={`grid ${gridCols} gap-3`}>
            <div>
              <label className={labelClass}>{t('settings.name')} *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>{t('settings.identifier')} *</label>
              <input value={uniqueId} onChange={(e) => setUniqueId(e.target.value)} required
                className={inputClass} disabled={!!device?.id} />
            </div>
            <div>
              <label className={labelClass}>{t('settings.phone')}</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>{t('settings.model')}</label>
              <input list="device-model-suggestions" value={model} onChange={(e) => setModel(e.target.value)} className={inputClass} />
              <datalist id="device-model-suggestions">{KNOWN_MODELS.map((m) => <option key={m} value={m} />)}</datalist>
            </div>
            <div>
              <label className={labelClass}>{t('settings.category')}</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={selectClass}>
                <option value="">{t('settings.default')}</option>
                {categories.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>{t('settings.contact')}</label>
              <input value={contact} onChange={(e) => setContact(e.target.value)} className={inputClass}
                placeholder={t('settings.contactHint')} />
            </div>
          </div>
        </div>

        <div>
          <h4 className={sectionClass}>{t('settings.configuration')}</h4>
          <div className={`grid ${gridCols} gap-3`}>
            <div>
              <label className={labelClass}>{t('settings.group')}</label>
              <select value={groupId} onChange={(e) => setGroupId(Number(e.target.value) || 0)} className={selectClass}>
                <option value={0}>{t('settings.none')}</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>{t('settings.calendar')}</label>
              <select value={calendarId} onChange={(e) => setCalendarId(Number(e.target.value) || 0)} className={selectClass}>
                <option value={0}>{t('settings.none')}</option>
                {calendars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>{t('settings.mapMarker')}</label>
              <button type="button" onClick={() => setShowMarkerPicker(true)}
                className="w-full h-9 px-3 text-xs rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 bg-white flex items-center gap-2.5 hover:border-blue-400 transition-colors">
                {markerStyle === 'arrow' ? (
                  <svg viewBox="0 0 36 36" className="w-6 h-6 flex-shrink-0">
                    <polygon points="18,6 26,26 18,21 10,26" fill="#10b981"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="w-6 h-6 flex-shrink-0" fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    {getIconDef(markerIcon)?.paths.map((d, i) => <path key={i} d={d} />)}
                    {getIconDef(markerIcon)?.circles.map((c, i) => <circle key={i} cx={c.cx} cy={c.cy} r={c.r} />)}
                  </svg>
                )}
                <span className="flex-1 text-start text-slate-700 dark:text-slate-300 truncate">
                  {t(`settings.marker${markerStyle.charAt(0).toUpperCase() + markerStyle.slice(1)}`)}
                  {markerIcon && markerIcon !== 'custom' ? ` — ${t(getIconDef(markerIcon)?.label || '')}` : ''}
                  {markerIcon === 'custom' ? ` — ${t('mapIcon.custom')}` : ''}
                </span>
                <Edit3 size={13} className="text-slate-400" />
              </button>
            </div>

            <div className="col-span-full rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{t('mapLabels.title', 'Map labels')}</div>
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <input data-testid="device-label-override" type="checkbox" checked={!!labelStyle} onChange={(e) => setLabelStyle(e.target.checked ? defaultEditableStyle() : null)} />
                {t('mapLabels.override', 'Override for this vehicle')}
              </label>
              {!labelStyle && (
                <p className="text-[10px] text-slate-400">
                  {t('mapLabels.inheritsFrom', 'Inherits from {{source}}', {
                    source: inherited.source === 'group'
                      ? (groupsById?.[inherited.groupId]?.name || t('settings.group', 'Group'))
                      : inherited.source === 'org' ? t('mapLabels.sourceOrg', 'the organization default') : t('mapLabels.sourceDefault', 'the built-in style'),
                  })}
                </p>
              )}
              {labelStyle && <LabelStyleEditor value={labelStyle} onChange={setLabelStyle} device={device} rawAttributeKeys={rawAttributeKeys} />}
            </div>

            {isAdmin && (
              <>
                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer h-9">
                    <input type="checkbox" checked={disabled} onChange={(e) => setDisabled(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-500 focus:ring-blue-500/20" />
                    <span className="text-xs text-slate-700 dark:text-slate-300 font-medium">{t('settings.deviceDisabled')}</span>
                  </label>
                </div>
                <div>
                  <label className={labelClass}>{t('settings.expirationDate')}</label>
                  <input type="datetime-local"
                    value={expirationTime ? expirationTime.slice(0, 16) : ''}
                    onChange={(e) => setExpirationTime(e.target.value ? e.target.value + ':00.000+00:00' : '')}
                    className={inputClass} />
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {[
                      { label: t('settings.expEndOfMonth', 'End of month'), fn: () => { const d = new Date(); d.setMonth(d.getMonth() + 1, 0); d.setHours(23, 59); return d; } },
                      { label: t('settings.exp1Month', '1 month'), fn: () => { const d = new Date(); d.setMonth(d.getMonth() + 1); return d; } },
                      { label: t('settings.exp3Months', '3 months'), fn: () => { const d = new Date(); d.setMonth(d.getMonth() + 3); return d; } },
                      { label: t('settings.exp6Months', '6 months'), fn: () => { const d = new Date(); d.setMonth(d.getMonth() + 6); return d; } },
                      { label: t('settings.exp1Year', '1 year'), fn: () => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d; } },
                    ].map((preset) => (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() => setExpirationTime(preset.fn().toISOString())}
                        className="px-2 py-0.5 text-[10px] font-medium rounded-md bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-500/10 dark:hover:text-blue-400 transition-all"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <CustomFieldsSection
          ref={customFieldsRef}
          initialValues={device?.custom_fields || {}}
          isNewDevice={!device?.id}
        />

        {device?.id && true && (
          <div>
            <h4 className={sectionClass}>{t('settings.photos')}</h4>
            <DevicePhotos deviceId={device.id} userId={userId} />
          </div>
        )}

        {device?.id && isAdmin && (
          <div>
            <CanSignalsSection device={device} isAdmin={isAdmin} />
            <SensorConfigSection device={device} isAdmin={isAdmin} />
          </div>
        )}

        <FuelCalibrationSection device={device} isAdmin={isAdmin} />

        <div className="flex items-center gap-2 pt-1">
          <button type="submit" disabled={saving || !name.trim() || !uniqueId.trim()}
            className="h-9 px-4 text-xs font-semibold rounded-xl text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-40 flex items-center gap-1.5">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {device?.id ? t('settings.update') : t('settings.create')}
          </button>
          <button type="button" onClick={onCancel}
            className="h-9 px-4 text-xs font-semibold rounded-xl text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700">
            {t('settings.cancel')}
          </button>
          {isAdmin && device?.id && (
            <button type="button" onClick={handleOpenAccumulators}
              className="h-9 px-4 text-xs font-semibold rounded-xl text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center gap-1.5 ms-auto">
              <Settings2 size={13} />
              {t('settings.setAccumulators')}
            </button>
          )}
        </div>
      </form>

      {showMarkerPicker && (
        <MapMarkerPicker
          open={showMarkerPicker}
          onClose={() => setShowMarkerPicker(false)}
          currentStyle={markerStyle}
          currentIcon={markerIcon}
          currentCustomUrl={markerCustomUrl}
          deviceId={device?.id}
          onSave={({ style, icon, customUrl }) => {
            setMarkerStyle(style);
            setMarkerIcon(icon);
            setMarkerCustomUrl(customUrl);
            setShowMarkerPicker(false);
          }}
        />
      )}

      {showAccumulators && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowAccumulators(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl dark:shadow-black/50 p-6 w-full max-w-sm space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200">{t('settings.accumulators')}</h4>
              <button onClick={() => setShowAccumulators(false)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{t('settings.totalDistance')}</label>
                <input
                  type="number"
                  value={accumDistance}
                  onChange={(e) => setAccumDistance(e.target.value)}
                  className={inputClass} />
                <p className="text-[9px] text-slate-400 dark:text-slate-500 mt-1">{t('settings.distanceHint')}</p>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{t('settings.engineHours')}</label>
                <input
                  type="number"
                  value={accumHours}
                  onChange={(e) => setAccumHours(e.target.value)}
                  className={inputClass} />
                <p className="text-[9px] text-slate-400 dark:text-slate-500 mt-1">{t('settings.engineHoursHint')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button onClick={handleSaveAccumulators} disabled={accumSaving}
                className="h-9 px-4 text-xs font-semibold rounded-xl text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-40 flex items-center gap-1.5">
                {accumSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {t('settings.save')}
              </button>
              <button onClick={() => setShowAccumulators(false)}
                className="h-9 px-4 text-xs font-semibold rounded-xl text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700">
                {t('settings.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
